import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../Customer/Customer.css';

function UserIndex() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated, companyId, logout, token, permissions } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout]);

    const hasCreateAdministrationPermission = permissions.includes('Create Administration');
    const hasEditAdministrationPermission = permissions.includes('Edit Administration');
    const hasDeleteAdministrationPermission = permissions.includes('Delete Administration');

    const fetchTableData = async () => {
        setLoading(true);
        fetchData('UserApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching User Details:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = async () => {
        deleteData('UserApi', `${deleteItemId}`, token)
            .then(() => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('UserApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    return (
        <div className="customer-container">
            {loading && (
                <div className="loading-overlay">
                    <CircularProgress size={40} className="loading-spinner" />
                    <span className="loading-text">Loading...</span>
                </div>
            )}

            {/* Header */}
            <div className="page-header">
                <h1 className="page-title">Users</h1>
                <div className="header-actions">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <Link to="/userCreate" className="btn-primary">
                        <AddIcon fontSize="small" />
                        Create New
                    </Link>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <div className="table-wrapper">
                    <table className="customer-table">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>User Name</th>
                                <th>Contact Number</th>
                                <th>Email</th>
                                <th style={{ width: '150px', textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? (
                                filteredData.map((item, index) => (
                                    <tr key={item.ID} className={item.IsActive ? '' : 'inactive-row'}>
                                        <td>{index + 1}</td>
                                        <td>{item.FirstName}</td>
                                        <td>{item.LastName}</td>
                                        <td>{item.UserName}</td>
                                        <td>{item.ContactNumber}</td>
                                        <td>{item.Email}</td>
                                        <td>
                                            <div className="action-buttons">
                                                <Link to={`/userDetail/${item.ID}`} className="btn-icon btn-view" title="View Details">
                                                    <VisibilityIcon fontSize="small" />
                                                </Link>
                                                {hasEditAdministrationPermission && (
                                                    <Link to={`/userEdit/${item.ID}`} className="btn-icon btn-edit" title="Edit">
                                                        <EditIcon fontSize="small" />
                                                    </Link>
                                                )}
                                                {hasDeleteAdministrationPermission && (
                                                    <button
                                                        onClick={() => item.IsActive ? openConfirmDialog(item.ID) : handleActivate(item.ID)}
                                                        className={`btn-icon ${item.IsActive ? 'btn-delete' : 'btn-activate'}`}
                                                        title={item.IsActive ? 'Delete' : 'Activate'}
                                                    >
                                                        {item.IsActive ? <DeleteIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7">
                                        <div className="empty-state">
                                            <p>No users found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Delete User?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this user? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    <button onClick={closeConfirmDialog} className="btn-cancel">
                        Cancel
                    </button>
                    <button onClick={handleDelete} className="btn-confirm-delete">
                        Delete
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default UserIndex;
